import type React from 'react';
import { useOverlay } from '../../../contexts/OverlayContext';
import type { OverlayElement, OverlayPosition } from '../../../types/overlay';
import { OverlayErrorBoundary } from '../ErrorBoundary';

const getPositionStyles = (position: OverlayPosition): React.CSSProperties => {
  return {
    top: position.top,
    left: position.left,
    right: position.right,
    bottom: position.bottom,
  };
};

const OverlayElementRenderer: React.FC<{ element: OverlayElement }> = ({ element }) => {
  const elementStyles: React.CSSProperties = {
    zIndex: element.zIndex || 10001,
    ...getPositionStyles(element.position),
  };

  const elementClassName = `overlay-element ${element.interactive ? 'interactive' : ''} ${element.visible === false ? 'hidden' : ''}`;

  return (
    <div
      key={element.id}
      className={elementClassName}
      style={elementStyles}
      data-overlay-id={element.id}
    >
      {element.component}
    </div>
  );
};

/**
 * OverlayContainer Component
 * Phase 3: Enhanced with error boundaries and better state management
 * Renders all active overlay elements with their positioning and styling
 */
export const OverlayContainer: React.FC = () => {
  const { overlays, isVisible, isLoading, error } = useOverlay();

  // Don't render anything if not visible
  if (!(isVisible || isLoading)) {
    return null;
  }

  // Show error state if there's an error (but don't block the meeting)
  if (error) {
    console.warn('Overlay system error:', error);
    // Still try to render overlays, the error boundary will catch component-specific errors
  }

  return (
    <OverlayErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Critical overlay error:', { error, errorInfo });
        // Could integrate with error reporting service here
      }}
    >
      <div
        id="nextgen-meet-overlay-container"
        className={`overlay-container ${isLoading ? 'loading' : ''}`}
        role="presentation"
        aria-label="Meeting overlay content"
      >
        {overlays.map((overlay) => (
          <OverlayErrorBoundary key={`boundary-${overlay.id}`}>
            <OverlayElementRenderer key={overlay.id} element={overlay} />
          </OverlayErrorBoundary>
        ))}

        {/* Loading indicator for overlay system */}
        {isLoading && <div className="overlay-loading-indicator">Loading overlays...</div>}
      </div>
    </OverlayErrorBoundary>
  );
};
